import type { API } from 'chessboard/api/config/types';
import { Core } from 'chessboard/core/config/types';
import { ErrorCodes } from 'chessboard/model/error/types';
import { ChessError } from 'chessboard/model/error';
import { Game } from 'model/game/types';
import JCE from '@chesscom/js-chess-engine';
import { setTimestampOnNode } from 'chessboard/model/move/set-timestamp-on-node';
import { convertTimesToTimestamps } from 'chessboard/api/model/convert-times-to-timestamps';
import { convertNagsToAnnotations } from 'chessboard/model/annotation/convert-nags-to-annotations';

/**
 * Create the load method.
 */
export function createLoad(context: API.Context) {
  /**
   * Load a game from FEN or PGN.
   */
  return function load(
    {
      fen,
      moves,
      pgn,
      times,
      timestamps,
      timeControl,
      variant = Game.Variants.Chess,
      pieces,
    }: API.Options.Load,
    option?: JCE.Options.LoadMoves,
  ) {
    const { api, game, respond } = context;

    let loaded = false;
    const beforePGN = game.pgn();

    if (pgn) {
      const convertedPgn = convertNagsToAnnotations(pgn);
      loaded = game.loadPgn(convertedPgn);
    } else if (fen) {
      loaded = game.load(fen, variant);
    }

    if (moves || moves === '') {
      loaded = game.loadMoves(moves, { autoPromote: true, ...option });
    }

    if (!loaded) {
      // moves are applied one at a time in JCE independently,
      // so if one move fails we need to reset to the original state
      game.loadPgn(beforePGN);

      throw new ChessError({
        code: ErrorCodes.BadData,
        data: { fen, pgn },
        message: 'Unable to load with provided data.',
      });
    }

    const line = api.getLine(0);
    const timestampValues = times
      ? convertTimesToTimestamps(timeControl || api.timeControl.get(), times)
      : timestamps;

    if (timestampValues && line) {
      line.forEach((node, ndx) => {
        const timestamp = timestampValues[ndx];

        if (timestamp === undefined) {
          return;
        }

        setTimestampOnNode({ api, node, timestamp });
      });
    }

    if (timeControl) {
      api.timeControl.set(timeControl);
    }

    const result = api.getResult();

    api.setResult(result === Game.Results.None ? api.getCalculatedResult() : result);

    // game.moveDetails appends extra information about castling and en passant to the move node.
    // Doing this here ensures that info is available on the initial load.
    // eslint-disable-next-line no-underscore-dangle
    game._lines().forEach((l) => l.forEach((move) => game.moveDetails(move)));

    return respond(Core.APIEvents.Load, {
      ...JCE.getPositionDetails({
        game,
        skipValidation: true,
      }),
      ...(<JCE.Position.Info>JCE.getPositionInfo({ analysis: true, game })),
      line,
      pieces: pieces ?? api.getPieces(),
      variant,
    });
  };
}
